import * as React from "react"
import {
  TextAa,
  Clock,
  ListBullets,
  Percent,
  IconProps,
  CalendarBlank,
  Handshake,
  CurrencyCircleDollar,
  Briefcase,
  ChartLine,
  Bank,
} from "@phosphor-icons/react"
import { cva, VariantProps } from "class-variance-authority"
import { ComponentProps, memo } from "react"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { PeriodType, PeriodTypeQuery } from "@/types"

const Table = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithRef<"table">) => (
  <div className="relative w-full overflow-auto">
    <table
      ref={ref}
      className={cn(`w-full caption-bottom text-sm table-container`, className)}
      {...props}
    />
  </div>
)
Table.displayName = `Table`

const TableHeader = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithRef<"thead">) => (
  <thead
    ref={ref}
    className={cn(
      `[&_tr]:border-b [&_tr]:hover:bg-transparent [&_td[data-type=fixed]]:shadow-right-top-cell`,
      className
    )}
    {...props}
  />
)
TableHeader.displayName = `TableHeader`

const TableHeaderIcons = memo(
  ({
    ref,
    className,
    icon,
    ...props
  }: React.ComponentPropsWithoutRef<"svg"> & {
    ref?: React.RefObject<SVGElement>
    icon:
      | `text`
      | `options`
      | `percent`
      | `clock`
      | "calendar"
      | "handshake"
      | "currency-circle-dollar"
      | "briefcase"
      | "chartline"
      | "bank"
  } & IconProps) => {
    switch (icon) {
      case `text`:
        return <TextAa size={16} ref={ref} {...props} />
      case `options`:
        return <ListBullets size={16} ref={ref} {...props} />
      case `percent`:
        return <Percent size={16} ref={ref} {...props} />
      case `clock`:
        return <Clock size={16} ref={ref} {...props} />
      case `calendar`:
        return (
          <CalendarBlank
            ref={ref}
            size={16}
            format={`stroke`}
            weight={`thin`}
            className={cn(`text-secondary`, className)}
            {...props}
          />
        )
      case `handshake`:
        return (
          <Handshake
            ref={ref}
            size={16}
            format={`stroke`}
            weight={`thin`}
            className={cn(`text-secondary`, className)}
            {...props}
          />
        )
      case `currency-circle-dollar`:
        return (
          <CurrencyCircleDollar
            ref={ref}
            size={16}
            format={`outline`}
            weight={`regular`}
            className={cn(`text-secondary`, className)}
            {...props}
          />
        )
      case `briefcase`:
        return (
          <Briefcase
            ref={ref}
            size={16}
            format={`stroke`}
            weight={`thin`}
            className={cn(`text-secondary`, className)}
            {...props}
          />
        )
      case `chartline`:
        return (
          <ChartLine
            ref={ref}
            size={16}
            format={`stroke`}
            weight={`thin`}
            className={cn(`text-secondary`, className)}
            {...props}
          />
        )
      case `bank`:
        return (
          <Bank
            ref={ref}
            size={16}
            format={`stroke`}
            weight={`thin`}
            className={cn(`text-secondary`, className)}
            {...props}
          />
        )
      default:
        return null
    }
  }
)
TableHeaderIcons.displayName = `TableHeaderIcons`

const TableHeadWithIcons = ({
  ref,
  className,
  icon,
  children,
  ...props
}: React.ComponentPropsWithRef<"span"> &
  Pick<ComponentProps<typeof TableHeaderIcons>, "icon">) => (
  <span
    ref={ref}
    className={cn(`flex items-center gap-2`, className)}
    {...props}
  >
    <TableHeaderIcons icon={icon} />
    {children}
  </span>
)
TableHeadWithIcons.displayName = `TableHeadWithIcons`

const TableBody = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithRef<"tbody">) => (
  <tbody
    ref={ref}
    className={cn(
      `[&_tr:last-child]:border-0 [&_tr:last-child_td[data-type=fixed]]:shadow-right-bottom-cell`,
      className
    )}
    {...props}
  />
)
TableBody.displayName = `TableBody`

const TableFooter = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithRef<"tfoot">) => (
  <tfoot
    ref={ref}
    className={cn(
      `border-t border-t-gray-200 bg-muted/50 font-medium [&>tr]:last:border-b-0`,
      className
    )}
    {...props}
  />
)
TableFooter.displayName = `TableFooter`

const TableRow = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithRef<"tr">) => (
  <tr
    ref={ref}
    className={cn(
      `border-b border-b-gray-200 transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted`,
      className
    )}
    {...props}
  />
)
TableRow.displayName = `TableRow`

const TableHead = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithRef<"th">) => (
  <th
    ref={ref}
    className={cn(
      `h-12 px-1 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 [&:has([role=checkbox])]:max-w-6 [&:has([role=checkbox])]:w-6 [&:has([role=checkbox])]:min-h-10 [&:has([role=checkbox])]:flex [&:has([role=checkbox])]:items-center [&:has([role=checkbox])]:justify-center [&:has([role=checkbox])]:border-none`,
      className
    )}
    {...props}
  />
)
TableHead.displayName = `TableHead`

const TableCell = ({
  ref,
  className,
  readonly,
  ...props
}: React.ComponentPropsWithRef<"td"> & {
  readonly?: boolean
}) => (
  <td
    ref={ref}
    className={cn(
      `px-1 border-r border-r-gray-200 align-middle [&:has([role=checkbox])]:pr-0 [&:has([role=checkbox])]:max-w-6 [&:has([role=checkbox])]:w-6 [&:has([role=checkbox])]:min-h-10 [&:has([role=checkbox])]:flex [&:has([role=checkbox])]:items-center [&:has([role=checkbox])]:justify-center [&:has([role=checkbox])]:border-none`,
      {
        "text-secondary bg-gray-50 cursor-not-allowed": readonly,
      },
      className
    )}
    {...props}
  />
)
TableCell.displayName = `TableCell`

const FixedTableCell = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithRef<"td">) => (
  <TableCell
    ref={ref}
    data-type="fixed"
    style={{
      display: `flex`,
    }}
    className={cn(
      `sticky left-0 bg-white z-40 shadow-right-center-cell px-0 flex items-center justify-between`,
      className
    )}
    {...props}
  />
)
FixedTableCell.displayName = `FixedTableCell`

/**
 * @deprecated Use PeriodTableCellHeaderContent instead
 * The new component need to be used under a TableProvider @see TableProvider
 * You can use the PeriodTableCellHeaderContent component to display the period header content.
 * This component is deprecated because it is not used in the new table component.
 */
const PeriodTableCellContent = ({
  ref,
  className,
  queryPeriod,
  period,
  type,
  children,
  fiscal_year,
  ...props
}: React.ComponentPropsWithRef<"div"> & {
  queryPeriod: PeriodTypeQuery
  period: PeriodType
  type: PeriodType
  fiscal_year?: number | null
}) => {
  const { t: commonT } = useTranslation(`common`)
  return (
    <div
      ref={ref}
      {...props}
      className={cn(`flex flex-col items-center`, className)}
    >
      {fiscal_year && queryPeriod !== `all` && (
        <span className="text-xs text-blue-400">
          {commonT(`table.month_fy`, {
            fy: fiscal_year,
          })}
        </span>
      )}
      {queryPeriod === `all` ? (
        <span className="text-xs text-blue-400">
          {type.charAt(0).toUpperCase() + type.slice(1)}
        </span>
      ) : null}
      <span className="text-sm">{children}</span>
    </div>
  )
}
PeriodTableCellContent.displayName = `PeriodTableCellContent`

const tableCellContentVariants = cva(`flex min-h-10 text-sm w-full`, {
  variants: {
    direction: {
      vertical: `flex-col justify-start items-end gap-1 py-2`,
      horizontal: `flex-row justify-end items-center`,
    },
    type: {
      input: `min-w-40 max-w-52`,
      output: `min-w-40 pr-2`,
      totals: `pr-2`,
    },
  },
  defaultVariants: {
    direction: `horizontal`,
    type: `input`,
  },
})

type TableCellContentProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof tableCellContentVariants>

const TableCellContent = ({
  ref,
  className,
  direction,
  type,
  ...props
}: TableCellContentProps & {
  ref?: React.RefObject<HTMLDivElement>
}) => (
  <div
    ref={ref}
    {...props}
    className={cn(tableCellContentVariants({ direction, type }), className)}
  />
)
TableCellContent.displayName = `TableCellContent`

const rowHeaderVariants = cva(
  `min-w-44 flex justify-start min-h-10 text-sm w-max`,
  {
    variants: {
      direction: {
        vertical: `flex-col items-start gap-1 py-2`,
        horizontal: `flex-row items-center`,
      },
      type: {
        input: `px-0`,
        output: `px-2`,
        totals: `px-2`,
      },
    },
    defaultVariants: {
      direction: `horizontal`,
      type: `input`,
    },
  }
)

type RowHeaderProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof rowHeaderVariants>

const RowHeader = ({
  ref,
  className,
  direction,
  type,
  ...props
}: React.ComponentPropsWithRef<"div"> & RowHeaderProps) => (
  <div
    ref={ref}
    {...props}
    className={rowHeaderVariants({ direction, type, className })}
  />
)
RowHeader.displayName = `RowHeader`

const TableCaption = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithRef<"caption">) => (
  <caption
    ref={ref}
    className={cn(`mt-4 text-sm text-muted-foreground`, className)}
    {...props}
  />
)
TableCaption.displayName = `TableCaption`

const TableEmpty = ({
  ref,
  children,
  ...props
}: React.ComponentPropsWithRef<"div">) => {
  const { className, ...rest } = props
  return (
    <div
      ref={ref}
      className={cn(`min-h-24 flex justify-center items-center`, className)}
      {...rest}
    >
      {children}
    </div>
  )
}
TableEmpty.displayName = `TableEmpty`

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableHeaderIcons,
  TableHeadWithIcons,
  TableRow,
  TableCell,
  TableCellContent,
  TableCaption,
  PeriodTableCellContent,
  FixedTableCell,
  RowHeader,
  TableEmpty,
}
